// The ring-colour popover, shared by the replay transport AND the live feed
// toolbar — one component, because the rules are one set (objects.ts owns
// them; live and replay differ only in which MODE they use) and two hand-kept
// copies of this UI would drift the way two legends always drift.
//
// Mode selection is RADIO ROWS in the same style as the Layers checkboxes —
// deliberately not a segmented button strip: that strip drew field-coloured
// buttons on a field-coloured popover and was invisible in practice, which
// cost three rounds of "I still can't pick the mode".

import type * as maplibregl from 'maplibre-gl';
import { type EventColorRules, eventColorRules, liveEventFactions,
         setEventColorRules } from './objects';

// Mid-tone hues that read on both themes.
const PALETTE = ['#3b82f6', '#ef4444', '#22c55e', '#eab308',
                 '#a855f7', '#f97316', '#06b6d4', '#ec4899'];

const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
   .replace(/"/g, '&quot;');

export interface ColorsPopover {
  el: HTMLElement;
  toggle(): void;
  hide(): void;
}

/** Mount the popover into `host` (which becomes the positioning context).
 *  `extraClass` picks the placement variant — the transport centres it above
 *  the bar, the feed drops it below the toolbar. */
export function createColorsPopover(host: HTMLElement,
                                    getMap: () => maplibregl.Map | null,
                                    extraClass = ''): ColorsPopover {
  if (getComputedStyle(host).position === 'static') {
    host.style.position = 'relative';
  }
  const el = document.createElement('div');
  el.className = `tt-clipbox tt-colorsbox${extraClass ? ` ${extraClass}` : ''}`;
  el.hidden = true;
  host.appendChild(el);

  // Radios pick the MODE per surface; swatches assign colours. Separate
  // events on purpose — a radio fires `change`, a swatch is a click.
  el.addEventListener('change', (ev) => {
    const r = ev.target as HTMLInputElement;
    if (!r || (r.name !== 'cm-live' && r.name !== 'cm-replay')) return;
    const rules = eventColorRules();
    const value = r.value as EventColorRules['liveMode'];
    if (r.name === 'cm-live') rules.liveMode = value;
    else rules.replayMode = value;
    const map = getMap();
    if (map) setEventColorRules(map, rules);
    render(el);
  });
  el.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    if (target.closest('[data-close]')) { el.hidden = true; return; }
    const sw = target.closest<HTMLElement>('.tt-sw');
    if (!sw) return;
    const rules = eventColorRules();
    const color = sw.dataset.c || null;
    const t = sw.dataset.t!;
    if (t === 'attacks') rules.attacks = color;
    else if (t === 'effects') rules.effects = color;
    else if (t.startsWith('f:')) {
      const name = t.slice(2);
      if (color) rules.factions[name] = color;
      else delete rules.factions[name];
    }
    const map = getMap();
    if (map) setEventColorRules(map, rules);
    render(el);
  });

  return {
    el,
    toggle(): void {
      if (el.hidden) {
        render(el);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    },
    hide(): void { el.hidden = true; },
  };
}

function render(box: HTMLElement): void {
  const rules = eventColorRules();
  const row = (label: string, current: string | null, target: string): string =>
    `<div class="tt-crow"><span class="n" title="${esc(label)}">${esc(label)}</span>
      <span class="tt-swatches">${PALETTE.map((c) =>
        `<button class="tt-sw${current === c ? ' on' : ''}" data-c="${c}"
                 data-t="${esc(target)}" style="background:${c}"></button>`).join('')
      }<button class="tt-sw none${current ? '' : ' on'}" data-c=""
               data-t="${esc(target)}" title="no colour">✕</button></span></div>`;
  const modeRows = (group: 'cm-live' | 'cm-replay', current: string): string =>
    ([['age', 'Age (default) — the ring follows the age ramp'],
      ['faction', 'By faction — each attributed faction’s colour'],
      ['class', 'By attack / impact — one colour per class']] as const)
      .map(([value, label]) =>
        `<label class="tt-lyr"><input type="radio" name="${group}" value="${value}"${
          current === value ? ' checked' : ''} /> ${esc(label)}</label>`).join('');
  const classInUse = rules.liveMode === 'class' || rules.replayMode === 'class';
  const factionInUse = rules.liveMode === 'faction' || rules.replayMode === 'faction';
  const factions = liveEventFactions();
  box.innerHTML =
    `<div class="tt-boxhead">Ring colour
       <button class="tt-x" data-close="1" title="Close">✕</button></div>`
    + `<div class="tt-chead">Live map colours by</div>`
    + modeRows('cm-live', rules.liveMode)
    + `<div class="tt-chead">Replay colours by</div>`
    + modeRows('cm-replay', rules.replayMode)
    + `<div class="tt-cnote">Only the ring takes the colour — the dot's fill
        always stays the age ramp.</div>`
    + `<div class="tt-csec">
        <div class="tt-chead">Attack / impact colours${
          classInUse ? '' : ' — not in use'}</div>`
    + row('Attacks — launch, strike, shelling', rules.attacks, 'attacks')
    + row('Impacts — impact, explosion, fire', rules.effects, 'effects')
    + `</div>`
    + `<div class="tt-csec">
        <div class="tt-chead">Faction colours${
          factionInUse ? '' : ' — not in use'}</div>`
    + (factions.length
      ? factions.map((f) =>
          row(f, rules.factions[f.toLowerCase()] ?? null, `f:${f.toLowerCase()}`)).join('')
      : `<div class="tt-cnone">No attributed factions among the loaded events.</div>`)
    + `</div>`;
}
